/**
 * Where an uploaded prototype lives on the sandbox origin.
 *
 * ============================================================================
 * THE TRAILING SLASH IS LOAD-BEARING
 * ============================================================================
 * The version URL is `<sandbox origin>/<prototypeId>/<versionId>/` and the
 * final slash is not cosmetic. Two things break without it:
 *
 *  1. The file server's entry route matches `/:prototypeId/:versionId` and
 *     redirects a slash-less request with a 308, costing a round trip on every
 *     single load.
 *  2. Far worse: a browser resolves a document's relative URLs against
 *     everything up to the *last* slash of its own URL. Loaded from
 *     `/p/v` the base is `/p/`, so the prototype's own `assets/app.css`
 *     resolves to `/p/assets/app.css` — one level too high, on a path that
 *     belongs to no version. Every stylesheet, script and image in every
 *     prototype would 404.
 *
 * The entry file is deliberately NOT appended. The file server resolves the
 * bare version path to the version's recorded `entryPath`, which is the only
 * component that knows what that file actually is; appending `index.html` here
 * would guess, and would guess wrong for any archive whose entry is nested.
 */

/** Trailing slashes trimmed, so joining never produces a doubled separator. */
const trimTrailingSlashes = (value: string): string =>
  value.replace(/\/+$/, '');

/** Leading `./`, `../` and `/` removed, so a path can only ever be relative. */
const toRelativePath = (value: string): string =>
  value.replace(/^(?:\.{1,2}\/)+/, '').replace(/^\/+/, '');

/**
 * The base URL of one uploaded version, with its mandatory trailing slash.
 *
 * Ids are percent-encoded even though they are UUIDs today: they are values
 * that arrive from a URL, and building a URL by concatenating unencoded input
 * is the kind of thing that is only safe until the id format changes.
 */
export const buildVersionBaseUrl = (
  sandboxOrigin: string,
  prototypeId: string,
  versionId: string,
): string =>
  `${trimTrailingSlashes(sandboxOrigin)}/${encodeURIComponent(prototypeId)}/${encodeURIComponent(versionId)}/`;

/**
 * The URL of one screen inside a version — what "Open in new tab" points at.
 *
 * A `null` or empty screen path yields the version base, which the file server
 * answers with the entry document. A path with a hash keeps it: `#billing` is
 * part of where the reviewer is in a single-page prototype, and dropping it
 * would open a different screen than the one on the toolbar.
 *
 * Resolution goes through `URL` rather than string concatenation so that a
 * nested screen (`pages/about.html`) and an absolute-looking one
 * (`/pages/about.html`) both land inside the version, never at the root of the
 * sandbox origin.
 */
export const buildScreenUrl = (
  sandboxOrigin: string,
  prototypeId: string,
  versionId: string,
  screenPath: string | null,
): string => {
  const base = buildVersionBaseUrl(sandboxOrigin, prototypeId, versionId);
  const relative = screenPath === null ? '' : toRelativePath(screenPath.trim());

  if (relative === '') {
    return base;
  }

  try {
    return new URL(relative, base).href;
  } catch {
    // An unparsable path is a bug elsewhere, not a reason to strand the user on
    // a dead link: fall back to the version's entry document.
    return base;
  }
};
